'use client';

import {
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ClipboardDocumentIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Component, ReactNode } from 'react';

import { Button } from '@/shared/ui/button';
import { Footer } from '@/widgets/footer';
import { Header } from '@/widgets/header';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallbackUI?: ReactNode | ((error: Error | null) => ReactNode);
	showFooter?: boolean;
	showHeader?: boolean;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: {
		url: string;
		userAgent: string;
		time: string;
		windowSize: string;
	} | null;
	showLog: boolean;
	copied: boolean;
}

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			showLog: false,
			copied: false,
		};
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		const errorInfo = {
			url: typeof window !== 'undefined' ? window.location.href : '',
			time: new Date().toISOString(),
			userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
			windowSize: `${window.innerWidth}x${window.innerHeight}`,
		};

		return {
			hasError: true,
			error,
			errorInfo,
		};
	}

	handleReset = (): void => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
			showLog: false,
			copied: false,
		});
	};

	toggleShowLog = (): void => {
		this.setState((prevState) => ({
			showLog: !prevState.showLog,
		}));
	};

	copyErrorToClipboard = (): void => {
		const { error, errorInfo } = this.state;
		if (error) {
			let errorText = `Сообщение ошибки: ${error.message}\n`;

			if (errorInfo) {
				errorText += `URL: ${errorInfo.url}\n`;
				errorText += `Время: ${errorInfo.time}\n`;
				errorText += `User Agent: ${errorInfo.userAgent}\n`;
				errorText += `Размер окна: ${errorInfo.windowSize}\n`;
			}

			errorText += `Стек: ${error.stack || 'недоступно'}`;
			navigator.clipboard.writeText(errorText);

			this.setState({ copied: true });
			setTimeout(() => {
				this.setState({ copied: false });
			}, 2000);
		}
	};

	render(): ReactNode {
		const { hasError, error, showLog, copied } = this.state;
		const { children, fallbackUI, showFooter, showHeader } = this.props;

		if (hasError) {
			if (fallbackUI) {
				return typeof fallbackUI === 'function'
					? fallbackUI(error)
					: fallbackUI;
			}

			// Дефолтный UI для ошибки
			return (
				<>
					{showHeader && <Header />}
					<div className="mx-auto flex w-full max-w-xl flex-1 items-center justify-center px-4 py-8">
						<div className="relative flex min-h-[300px] w-full flex-1 flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
							<ExclamationTriangleIcon className="mb-4 h-12 w-12 text-red-500" />
							<h2 className="mb-2 text-xl font-semibold text-gray-900">
								Упс, у нас что-то сломалось
							</h2>
							<p className="mb-6 text-gray-600">
								Попробуйте обновить страницу, если ошибка повторится,
								пожалуйста, свяжитесь с нами.
							</p>

							<div className="flex flex-col gap-3 md:flex-row">
								<Button href="/">Вернуться на главную</Button>

								<Button
									variant="outline"
									onClick={this.toggleShowLog}
									tabIndex={0}
									aria-label={
										showLog
											? 'Скрыть подробности ошибки'
											: 'Показать подробности ошибки'
									}
								>
									{showLog ? (
										<>
											<ChevronUpIcon className="h-4 w-4" />
											Скрыть
										</>
									) : (
										<>
											<ChevronDownIcon className="h-4 w-4" />
											Связаться с поддержкой
										</>
									)}
								</Button>
							</div>

							{showLog && (
								<div className="mt-6 w-full animate-[slideUp_0.3s_ease-in-out]">
									<p className="mb-4 rounded-md bg-indigo p-4 text-left text-white shadow">
										Для связи, пожалуйста, скопируйте текст ошибки и отправьте
										его
										<a
											href="https://t.me/Karina_algocode_io"
											target="_blank"
											rel="noopener noreferrer"
											className="ml-1 text-white underline"
										>
											Карине (@Karina_algocode_io)
										</a>
									</p>
									<div className="relative rounded-md bg-gray-5 p-4 text-left">
										<div className="mb-2 flex items-center justify-between">
											<span className="font-medium text-gray-2">
												Информация об ошибке
											</span>
											<div className="flex gap-2">
												<Button
													variant="outline"
													onClick={this.copyErrorToClipboard}
													aria-label="Копировать ошибку"
												>
													{copied ? (
														<>
															<CheckIcon className="h-4 w-4 text-green-500" />
															<span className="text-green-500">
																Скопировано
															</span>
														</>
													) : (
														<>
															<ClipboardDocumentIcon className="h-4 w-4" />
															<span>Копировать</span>
														</>
													)}
												</Button>
											</div>
										</div>
										<pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-5 p-2 text-xs text-gray-800">
											{error ? (
												<>
													<strong>Сообщение:</strong>{' '}
													{error.message || 'Нет сообщения'}
													{error.stack && (
														<>
															<br />
															<br />
															<strong>Стек:</strong>
															<br />
															{error.stack}
														</>
													)}
												</>
											) : (
												'Информация об ошибке недоступна'
											)}
										</pre>
									</div>
								</div>
							)}
						</div>
					</div>
					{showFooter && <Footer />}
				</>
			);
		}
		return children;
	}
}
